import { Column } from "primereact/column";
import { SelectButton } from "primereact/selectbutton";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import MeaButton from "../../../../components/MeaButton";
import MeaDataTable from "../../../../components/MeaDataTable";
import CreateCourseModal from "./AddTeacherModal";
import teachersAPI from "../../../../services/teachers";
import sessionsAPI from "../../../../services/sessions";
import MeaTeacherAvatar from "../../components/MeaTeacherAvatar";
import { useNavigate, useParams } from "react-router-dom";
import { PERMISSION_TRAINING_TASKS, PERMISSION_TRAINING_TEACHERS } from "../../../../permissions";
import { useAuthContext } from "../../../../hooks/useAuthContext";
import { ToFormatedShortISODate } from "../../../../utils/dates";
import { getTeacherMonthlyStats } from "../teacherSessionStats";

const TeachersListComponent = forwardRef((props, ref) => {
  const { showAddButton, training, onMonthChange } = props;
  const { hasPermission } = useAuthContext();
  const navigate = useNavigate();
  const [page, setPage] = useState();
  const [pageSize, setPageSize] = useState();
  const [selectedMonth, setSelectedMonth] = useState(null);

  const params = useParams();
  const schoolId = params.schoolId;
  const trainingId = params.trainingId;
  const accessToken = "";
  const createModalRef = useRef();
  const canDeleteTeachers = hasPermission(PERMISSION_TRAINING_TEACHERS.DELETE);

  const queryList = teachersAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    page,
    pageSize
  );

  const monthOptions = useMemo(() => {
    const startDate = training?.start_courses_date ? new Date(training.start_courses_date) : new Date();
    const endDate = training?.end_courses_date ? new Date(training.end_courses_date) : new Date();
    const months = [];
    const loopDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);

    while (loopDate <= endDate) {
      const year = loopDate.getFullYear();
      const month = loopDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      months.push({
        label: firstDay.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
        value: `${year}-${String(month + 1).padStart(2, "0")}`,
        start_date: ToFormatedShortISODate(firstDay),
        end_date: ToFormatedShortISODate(lastDay),
      });

      loopDate.setMonth(loopDate.getMonth() + 1);
    }

    if (months.length === 0) {
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      months.push({
        label: firstDay.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
        value: `${year}-${String(month + 1).padStart(2, "0")}`,
        start_date: ToFormatedShortISODate(firstDay),
        end_date: ToFormatedShortISODate(lastDay),
      });
    }

    return months;
  }, [training?.start_courses_date, training?.end_courses_date]);

  const currentMonthValue = useMemo(() => {
    const currentDate = new Date();
    return `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;
  }, []);

  const selectedMonthData = monthOptions.find((month) => month.value === selectedMonth) || monthOptions[0];

  useEffect(() => {
    onMonthChange?.(selectedMonthData?.value);
  }, [onMonthChange, selectedMonthData?.value]);

  useEffect(() => {
    if (!monthOptions[0]) {
      return;
    }

    if (!selectedMonth) {
      const defaultMonth =
        monthOptions.find((month) => month.value === currentMonthValue) || monthOptions[0];
      setSelectedMonth(defaultMonth.value);
      return;
    }

    const selectedMonthStillAvailable = monthOptions.some((month) => month.value === selectedMonth);
    if (!selectedMonthStillAvailable) {
      setSelectedMonth(monthOptions[0].value);
    }
  }, [currentMonthValue, monthOptions, selectedMonth]);

  const monthlySessionsFilter = useMemo(
    () => ({
      start_date: selectedMonthData?.start_date,
      end_date: selectedMonthData?.end_date,
    }),
    [selectedMonthData?.start_date, selectedMonthData?.end_date]
  );

  const sessionsQuery = sessionsAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000,
    monthlySessionsFilter
  );

  const isLoadingMonthlyData = sessionsQuery?.isFetching;

  const teachersWithMonthlyStats = useMemo(() => {
    return (queryList?.data?.data || []).map((teacher) =>
      getTeacherMonthlyStats(teacher, sessionsQuery?.data?.data || [])
    );
  }, [queryList?.data?.data, sessionsQuery?.data?.data]);

  const tableQuery = useMemo(() => {
    if (!queryList?.data) {
      return queryList;
    }

    return {
      ...queryList,
      data: {
        ...queryList.data,
        data: teachersWithMonthlyStats,
      },
    };
  }, [queryList, teachersWithMonthlyStats]);

  useImperativeHandle(ref, () => ({
    refresh: () => {
      queryList?.refetch();
      sessionsQuery?.refetch();
    },
  }));

  const startToolbarTemplate = (
    <div>
      {showAddButton && (
        <>
          <MeaButton
            label="Ajouter un(e) enseignant(e)"
            icon="pi pi-plus"
            size="small"
            className="p-button mr-2 action"
            onClick={() => {
              createModalRef.current.show(() => {
                queryList.refetch();
              });
            }}
            data-pr-tooltip="Nouveau niveau"
            permission={PERMISSION_TRAINING_TEACHERS.CREATE}
          />
          <MeaButton disabled={true} icon="pi pi-cloud-upload" label="Importer" size="small" className="p-button mr-2" outlined
            onClick={() => {
              navigate("/school/" + schoolId + "/admin/trainings/" + trainingId + "/teachers-import");
            }}
            data-pr-tooltip="Importer"
            permission={PERMISSION_TRAINING_TASKS.IMPORT_FROM_TRAINING}
          /></>
      )}
    </div>
  );

  return (
    <>
      <CreateCourseModal ref={createModalRef} schoolId={schoolId} trainingId={trainingId} accessToken={accessToken} />
      <div className="mb-3">
        <SelectButton
          value={selectedMonth || selectedMonthData?.value}
          options={monthOptions}
          optionLabel="label"
          optionValue="value"
          onChange={(e) => setSelectedMonth(e.value)}
          className="w-full flex-wrap"
        />
      </div>
      <MeaDataTable
        query={tableQuery}
        loading={isLoadingMonthlyData}
        serviceAPI={teachersAPI}
        schoolId={schoolId}
        trainingId={trainingId}
        accessToken={accessToken}
        names={{
          pagination: "teachers",
          singular: "enseignant(e)",
          plural: "enseignant(e)s",
        }}
        startToolbarTemplate={startToolbarTemplate}
        showDeleteButton={canDeleteTeachers}
        showSelection={canDeleteTeachers}
        setPage={setPage}
        setPageSize={setPageSize}
      >
        {canDeleteTeachers && (
          <Column selectionMode="multiple" headerStyle={{ width: "3rem" }}></Column>
        )}
        <Column
          header="Enseignant(e)"
          body={(teacher) => <MeaTeacherAvatar teacher={teacher} permission={PERMISSION_TRAINING_TEACHERS.GET} schoolId={schoolId} trainingId={trainingId} />}
        ></Column>
        <Column
          header="Nombre de cours par semaine"
          body={(teacher) => teacher?.schedule_items?.length}
        ></Column>
        <Column
          header="Séances prévues du mois"
          body={(teacher) => teacher?.planned_sessions_count || 0}
        ></Column>
        <Column
          header="Séances effectives du mois"
          body={(teacher) => teacher?.effective_sessions_count || 0}
        ></Column>
        <Column
          header="Total de points du mois"
          body={(teacher) =>
            new Intl.NumberFormat("fr-FR", {
              maximumFractionDigits: 2,
            }).format(teacher?.effective_points_total || 0)
          }
        ></Column>
        <Column
          rowEditor={false}
          headerStyle={{ width: "10%", minWidth: "8rem" }}
          bodyStyle={{ textAlign: "center" }}
        ></Column>
      </MeaDataTable>
    </>
  );
});

export default TeachersListComponent;
